// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 39-gcp-installation.md
// @greenlight-spec: decisions/0031-shared-wizard-settings-editors.md
import * as React from 'react';
import { AlertTriangle, Check, CircleDashed } from 'lucide-react';

import { SectionCard } from '@/components/greenlight/SectionCard';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import type { StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { relativeTime } from '@/components/greenlight/relativeTime';
import {
  ContactDisclosure,
  ContactEditor,
  useContactEditor,
} from '@/components/greenlight/org/ContactEditor';
import {
  DisplayNameEditor,
  useDisplayNameEditor,
} from '@/components/greenlight/org/DisplayNameEditor';
import {
  SupportedAgentsEditor,
  useSupportedAgentsEditor,
} from '@/components/greenlight/org/SupportedAgentsEditor';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { AgentKind, CloudProvider, InstallContact, OrgSettings } from '@greenlight/shared';

import { CopyRow } from './CopyRow';
import { SettingsSectionHeading } from './SettingsSectionHeading';
import { CursorAdminKeyRow, type CursorAdminKeyRowProps } from './CursorAdminKeyRow';
import { ToggleRow } from './ToggleRow';

// ── Types ────────────────────────────────────────────────────────────────────

export interface OrgSettingsProps {
  org: OrgSettings['org'];
  onSaveDisplayName: (displayName: string) => Promise<void> | void;
  /** PATCH /api/settings/contact (docs/12 §Install contact) — org_admin only. */
  onSaveContact: (contact: InstallContact) => Promise<void> | void;
  onToggleFeedbackAutoShare: (autoShare: boolean) => void | Promise<void>;
  /** PATCH /api/settings/crash-log-sharing (ADR-0069) — org_admin only. */
  onToggleCrashLogAutoShare: (autoShare: boolean) => void | Promise<void>;
  /** PATCH /api/settings/feedback-observer-reports (docs/28) — org_admin only. */
  onToggleObserverReports: (enabled: boolean) => void | Promise<void>;
  /** PATCH /api/settings/agent-token-usage-reports (docs/28) — org_admin only. */
  onToggleTokenUsageReports: (enabled: boolean) => void | Promise<void>;
  /** PUT /api/settings/agent-token-usage/cursor-admin-key (docs/28) — org_admin only. */
  onSetCursorAdminKey: CursorAdminKeyRowProps['onSetKey'];
  /** DELETE /api/settings/agent-token-usage/cursor-admin-key (docs/28) — org_admin only. */
  onRemoveCursorAdminKey: CursorAdminKeyRowProps['onRemoveKey'];
  /** PATCH /api/settings/app-discoverability (docs/42 §Discoverability) — org_admin only. */
  onToggleDiscoverabilityDefault: (discoverableByDefault: boolean) => Promise<void> | void;
  /** PATCH /api/settings/supported-agents (ADR-0040) — org_admin only. */
  onSaveSupportedAgents: (supportedAgents: AgentKind[]) => Promise<void> | void;
  className?: string;
}

// ── DNS step metadata ─────────────────────────────────────────────────────────

// Canonical provisioning order. The checklist always renders all of these; a step
// absent from the payload is treated as still pending, never silently dropped.
const DNS_STEPS = ['zone_delegation', 'records_created', 'cert_issued', 'healthz'] as const;
type DnsStep = (typeof DNS_STEPS)[number];

interface StepMeta {
  label: string;
  description: (cpUrl: string, cloud: CloudProvider) => string;
}

// The cloud's managed-DNS service name, for the zone-delegation description (docs/39).
const DNS_SERVICE_NAME: Record<CloudProvider, string> = {
  azure: 'Azure DNS',
  gcp: 'Cloud DNS',
  aws: 'Route 53',
  localfake: 'the local DNS fake',
};

// Labels are state-neutral nouns — the per-row status word (Passed/Failed/Pending)
// carries the outcome, so a failed step never reads as if it succeeded.
const DNS_STEP_META: Record<DnsStep, StepMeta> = {
  zone_delegation: {
    label: 'Zone delegation',
    description: (_cpUrl, cloud) => `NS records delegated to ${DNS_SERVICE_NAME[cloud]}`,
  },
  records_created: {
    label: 'Apex & wildcard records',
    description: () => 'A records provisioned inside the zone',
  },
  cert_issued: {
    label: 'Control-plane certificate',
    description: (cpUrl) => {
      try {
        return `TLS active for ${new URL(cpUrl).host}`;
      } catch {
        return 'TLS active for control plane';
      }
    },
  },
  healthz: {
    label: 'Health check',
    description: () => 'GET /healthz → 200',
  },
};

// Overall readiness for the card badge, derived from the step outcomes: a step
// recorded as not-ok needs attention; "Ready" requires every canonical step to be
// recorded AND passed, so a pending or entirely-missing step reads as in progress.
type DnsHistory = NonNullable<NonNullable<OrgSettings['org']>['dns_https_history']>;

function dnsReadiness(history: DnsHistory): { variant: StatusBadgeVariant; label: string } {
  if (history.some((e) => e.at !== null && !e.ok)) {
    return { variant: 'failed', label: 'Action needed' };
  }
  const passed = new Set(history.filter((e) => e.ok && e.at !== null).map((e) => e.step));
  const allPassed = DNS_STEPS.every((step) => passed.has(step));
  return allPassed
    ? { variant: 'active', label: 'Ready' }
    : { variant: 'degraded', label: 'In progress' };
}

// ── DNS checklist row ─────────────────────────────────────────────────────────

interface DnsRowProps {
  label: string;
  description: string;
  ok: boolean;
  at: string | null;
}

function DnsRow({ label, description, ok, at }: DnsRowProps): React.JSX.Element {
  // Three states: pending (at===null), success (ok && at present), failed (!ok && at present)
  const isPending = at === null;
  const isSuccess = !isPending && ok;

  const iconNode = isPending ? (
    // Pending — muted dashed clock glyph; color is never the only signal (glyph + "Pending" text)
    <span
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted"
      aria-hidden="true"
    >
      <CircleDashed className="h-3.5 w-3.5 text-muted-foreground" />
    </span>
  ) : isSuccess ? (
    // Success — green check circle; named utility bg-gl-green/10 + text-gl-green-text
    <span
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gl-green/10"
      aria-hidden="true"
    >
      <Check className="h-3.5 w-3.5 text-gl-green-text" />
    </span>
  ) : (
    // Failed — red triangle; text-gl-red-text; paired with label text (color not sole signal)
    <span
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gl-red/10"
      aria-hidden="true"
    >
      <AlertTriangle className="h-3.5 w-3.5 text-gl-red-text" />
    </span>
  );

  // State is conveyed by an explicit word, never by color or icon alone (WCAG 1.4.1).
  const statusWord = isPending ? 'Pending' : isSuccess ? 'Passed' : 'Failed';
  const statusColor = isPending
    ? 'text-muted-foreground'
    : isSuccess
      ? 'text-gl-green-text'
      : 'text-gl-red-text';

  return (
    <div className="flex items-center gap-3 px-5 py-3.5">
      {iconNode}

      {/* Label + description */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span
          className={cn(
            'text-sm font-medium',
            isPending ? 'text-muted-foreground' : 'text-foreground',
          )}
        >
          {label}
        </span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </div>

      {/* Explicit state word (text) + relative time once the step has a recorded outcome */}
      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <span className={cn('font-mono text-xs font-medium', statusColor)}>{statusWord}</span>
        {at !== null && (
          <span className="font-mono text-[11px] text-muted-foreground">{relativeTime(at)}</span>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function OrgSettings({
  org,
  onSaveDisplayName,
  onSaveContact,
  onToggleDiscoverabilityDefault,
  onToggleFeedbackAutoShare,
  onToggleCrashLogAutoShare,
  onToggleObserverReports,
  onToggleTokenUsageReports,
  onSetCursorAdminKey,
  onRemoveCursorAdminKey,
  onSaveSupportedAgents,
  className,
}: OrgSettingsProps): React.JSX.Element {
  return (
    <div className={cn('space-y-6', className)}>
      <SettingsSectionHeading title="Organization" pill="Org admin" />

      {/* The org section is org-admin-only (docs/28 §/settings); a non-admin GET
          omits it, so the wiring page only mounts this with `org` present. Guard
          the undefined case anyway, matching IdentitySettings/SourceControlSettings. */}
      {org === undefined ? (
        <div className="rounded-gl-lg border border-border bg-card overflow-hidden">
          <div className="px-5 py-4 text-sm text-muted-foreground">Not configured yet.</div>
        </div>
      ) : (
        <ConfiguredOrgSettings
          org={org}
          onSaveDisplayName={onSaveDisplayName}
          onSaveContact={onSaveContact}
          onToggleDiscoverabilityDefault={onToggleDiscoverabilityDefault}
          onToggleFeedbackAutoShare={onToggleFeedbackAutoShare}
          onToggleCrashLogAutoShare={onToggleCrashLogAutoShare}
          onToggleObserverReports={onToggleObserverReports}
          onToggleTokenUsageReports={onToggleTokenUsageReports}
          onSetCursorAdminKey={onSetCursorAdminKey}
          onRemoveCursorAdminKey={onRemoveCursorAdminKey}
          onSaveSupportedAgents={onSaveSupportedAgents}
        />
      )}
    </div>
  );
}

// ── Configured body ───────────────────────────────────────────────────────────

interface ConfiguredOrgSettingsProps {
  org: NonNullable<OrgSettings['org']>;
  onSaveDisplayName: OrgSettingsProps['onSaveDisplayName'];
  onSaveContact: OrgSettingsProps['onSaveContact'];
  onToggleDiscoverabilityDefault: OrgSettingsProps['onToggleDiscoverabilityDefault'];
  onToggleFeedbackAutoShare: OrgSettingsProps['onToggleFeedbackAutoShare'];
  onToggleCrashLogAutoShare: OrgSettingsProps['onToggleCrashLogAutoShare'];
  onToggleObserverReports: OrgSettingsProps['onToggleObserverReports'];
  onToggleTokenUsageReports: OrgSettingsProps['onToggleTokenUsageReports'];
  onSetCursorAdminKey: OrgSettingsProps['onSetCursorAdminKey'];
  onRemoveCursorAdminKey: OrgSettingsProps['onRemoveCursorAdminKey'];
  onSaveSupportedAgents: OrgSettingsProps['onSaveSupportedAgents'];
}

function ConfiguredOrgSettings({
  org,
  onSaveDisplayName,
  onSaveContact,
  onToggleDiscoverabilityDefault,
  onToggleFeedbackAutoShare,
  onToggleCrashLogAutoShare,
  onToggleObserverReports,
  onToggleTokenUsageReports,
  onSetCursorAdminKey,
  onRemoveCursorAdminKey,
  onSaveSupportedAgents,
}: ConfiguredOrgSettingsProps): React.JSX.Element {
  const displayNameEditor = useDisplayNameEditor(org.display_name);
  const [saving, setSaving] = React.useState(false);
  const canSave = displayNameEditor.isDirty && displayNameEditor.isValid;
  const [discoverableByDefault, setDiscoverableByDefault] = React.useState(
    org.apps_discoverable_by_default,
  );
  const [feedbackAutoShare, setFeedbackAutoShare] = React.useState(org.feedback_auto_share);
  const [crashLogAutoShare, setCrashLogAutoShare] = React.useState(org.crash_log_auto_share);
  const [observerReports, setObserverReports] = React.useState(org.feedback_observer_reports);
  const [tokenUsageReports, setTokenUsageReports] = React.useState(org.agent_token_usage_reports);

  const supportedAgentsEditor = useSupportedAgentsEditor(org.supported_agents);
  const [savingAgents, setSavingAgents] = React.useState(false);
  const canSaveAgents = supportedAgentsEditor.isDirty && supportedAgentsEditor.isValid;

  const contactEditor = useContactEditor(org.contact_name ?? '', org.contact_email ?? '');
  const [savingContact, setSavingContact] = React.useState(false);

  React.useEffect(() => {
    setDiscoverableByDefault(org.apps_discoverable_by_default);
  }, [org.apps_discoverable_by_default]);

  React.useEffect(() => {
    setFeedbackAutoShare(org.feedback_auto_share);
  }, [org.feedback_auto_share]);

  React.useEffect(() => {
    setCrashLogAutoShare(org.crash_log_auto_share);
  }, [org.crash_log_auto_share]);

  React.useEffect(() => {
    setObserverReports(org.feedback_observer_reports);
  }, [org.feedback_observer_reports]);

  React.useEffect(() => {
    setTokenUsageReports(org.agent_token_usage_reports);
  }, [org.agent_token_usage_reports]);

  function handleCancel(): void {
    displayNameEditor.reset();
  }

  async function handleSave(): Promise<void> {
    const displayName = displayNameEditor.validate();
    if (displayName === null || !displayNameEditor.isDirty || saving) return;
    setSaving(true);
    try {
      await onSaveDisplayName(displayName);
    } finally {
      setSaving(false);
    }
  }

  function handleCancelAgents(): void {
    supportedAgentsEditor.reset();
  }

  async function handleSaveAgents(): Promise<void> {
    const supportedAgents = supportedAgentsEditor.validate();
    if (supportedAgents === null || !supportedAgentsEditor.isDirty || savingAgents) return;
    setSavingAgents(true);
    try {
      await onSaveSupportedAgents(supportedAgents);
    } finally {
      setSavingAgents(false);
    }
  }

  async function handleSaveContact(): Promise<void> {
    const contact = contactEditor.validate();
    if (contact === null || !contactEditor.isDirty || savingContact) return;
    setSavingContact(true);
    try {
      await onSaveContact(contact);
    } finally {
      setSavingContact(false);
    }
  }

  const hasDnsHistory = org.dns_https_history !== undefined && org.dns_https_history.length > 0;
  const dnsStatus = hasDnsHistory ? dnsReadiness(org.dns_https_history ?? []) : null;

  return (
    <>
      {/* ── Card 1: Display name + read-only install fields ─────────────────── */}
      <SectionCard headerBar title="Organization profile">
        {/* Form body */}
        <div className="px-5 pt-5 pb-4">
          <DisplayNameEditor editor={displayNameEditor} disabled={saving} />
        </div>

        {/* Read-only locked rows */}
        <div className="divide-y divide-border border-t border-border">
          <CopyRow
            label="Control-plane URL"
            value={org.control_plane_url}
            locked
            lockedHint="Set during install"
          />
          <CopyRow
            label="Primary domain"
            value={org.primary_domain}
            locked
            lockedHint="Set during install"
          />
          <CopyRow
            label="Apps subdomain"
            value={org.apps_subdomain}
            locked
            lockedHint="Set during install"
          />
        </div>

        {/* Footer bar */}
        <div className="flex items-center gap-2 border-t border-border bg-muted/20 px-5 py-3">
          {/* Left muted note */}
          <p className="flex-1 text-xs text-muted-foreground">
            Locked fields are set during install.
          </p>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="default"
              className="h-11 md:h-9"
              onClick={handleCancel}
              disabled={saving || !displayNameEditor.isDirty}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="default"
              className="h-11 md:h-9"
              onClick={() => void handleSave()}
              disabled={!canSave || saving}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      </SectionCard>

      {/* ── Card 2: Install contact ──────────────────────────────────────────
          Named in the wizard, changed here: people leave, and billing moves to
          a shared address. An install that predates the wizard step arrives
          with none, which the badge says out loud. */}
      <SectionCard
        headerBar
        title="Shift contact"
        badge={
          org.contact_email === null ? (
            <StatusBadge variant="degraded">Not set</StatusBadge>
          ) : undefined
        }
      >
        <div className="space-y-4 px-5 pt-5 pb-4">
          <ContactEditor
            editor={contactEditor}
            disabled={savingContact}
            idPrefix="settings-contact"
          />
          <ContactDisclosure />
          {contactEditor.error !== null && (
            <p role="alert" className="text-xs text-destructive" data-testid="contact-error">
              {contactEditor.error}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 border-t border-border bg-muted/20 px-5 py-3">
          <p className="flex-1 text-xs text-muted-foreground">
            Shift reaches this person about your trial and subscription.
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="default"
              className="h-11 md:h-9"
              onClick={() => contactEditor.reset()}
              disabled={savingContact || !contactEditor.isDirty}
              data-testid="cancel-contact"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="default"
              className="h-11 md:h-9"
              onClick={() => void handleSaveContact()}
              disabled={!contactEditor.isDirty || !contactEditor.isValid || savingContact}
              data-testid="save-contact"
            >
              {savingContact ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      </SectionCard>

      {/* ── Card 3: DNS & HTTPS readiness ───────────────────────────────────── */}
      <SectionCard
        headerBar
        title="DNS & HTTPS readiness"
        badge={
          dnsStatus ? (
            <StatusBadge variant={dnsStatus.variant}>{dnsStatus.label}</StatusBadge>
          ) : undefined
        }
      >
        {hasDnsHistory ? (
          <div className="divide-y divide-border">
            {/* Render the full canonical checklist; a step missing from the payload
                shows as pending rather than vanishing (and never counts as Ready). */}
            {DNS_STEPS.map((step) => {
              const entry = org.dns_https_history?.find((e) => e.step === step);
              const meta = DNS_STEP_META[step];
              return (
                <DnsRow
                  key={step}
                  label={meta.label}
                  description={meta.description(org.control_plane_url, org.cloud)}
                  ok={entry?.ok ?? false}
                  at={entry?.at ?? null}
                />
              );
            })}
          </div>
        ) : (
          <div className="px-5 py-4 text-sm text-muted-foreground">Not configured yet.</div>
        )}
      </SectionCard>

      {/* ── Card 4: Default app access ───────────────────────────────────────
          The marketplace is always available; what IT chooses here is the access
          a newly registered app starts with, in the same words the app's own
          Access tab uses. */}
      <SectionCard headerBar title="Default app access">
        <ToggleRow
          label="New apps are open to everyone in the org"
          helper="Open apps appear in the marketplace and anyone in the org can use them. Off means a new app starts restricted and private — only the people its owner lists can find or open it. Owners can change this per app; existing apps are unaffected."
          checked={discoverableByDefault}
          onCheckedChange={(checked) => {
            setDiscoverableByDefault(checked);
            void Promise.resolve(onToggleDiscoverabilityDefault(checked)).catch(() =>
              setDiscoverableByDefault(org.apps_discoverable_by_default),
            );
          }}
        />
      </SectionCard>

      {/* ── Card 5: Observer reports ────────────────────────────────────────
          The copy says what the hook reads and where the result goes, because
          the builder is never told in-session (docs/28). */}
      <SectionCard headerBar title="Agent reports">
        <ToggleRow
          label="Let the Claude Code plugin report Greenlight problems its agents work around"
          helper="On each builder's machine, the plugin reads the coding session after each turn. When an agent hit Greenlight errors and found a way through, the plugin sends only those Greenlight calls and their results, with ids and secrets removed, to a small Claude model through the builder's own Claude sign-in. If the model judges it a platform problem, the plugin files one summarized report per session into this organization's Feedback page. The rest of the conversation never leaves the machine, and nothing goes to Shift unless you send it or turn on sharing below. Builders are not told in the session."
          checked={observerReports}
          onCheckedChange={(checked) => {
            setObserverReports(checked);
            void Promise.resolve(onToggleObserverReports(checked)).catch(() =>
              setObserverReports(org.feedback_observer_reports),
            );
          }}
        />
      </SectionCard>

      {/* ── Card 6: Build token usage ───────────────────────────────────────
          Nothing here leaves the install; the copy says the figure is an
          equivalent so nobody reads it as a bill (docs/28). */}
      <SectionCard headerBar title="Build token usage">
        <ToggleRow
          label="Report build token usage"
          helper="When a coding session ends, the Greenlight plugin reads its token counts on the builder's machine and sends them to this install, so Cost can show token cost per app. Cursor keeps no counts on the machine, so its sessions are read from Cursor with the key below. Only counts per model and app are sent, never prompts, transcripts, or file paths, and nothing goes to Shift. Builders on seat plans are not charged per token, so the figure is what the same tokens would cost at the vendor's API prices, not a bill. Sessions before you turn this on are not counted."
          checked={tokenUsageReports}
          onCheckedChange={(checked) => {
            setTokenUsageReports(checked);
            void Promise.resolve(onToggleTokenUsageReports(checked)).catch(() =>
              setTokenUsageReports(org.agent_token_usage_reports),
            );
          }}
        />
        {org.cursor_usage && (
          <CursorAdminKeyRow
            cursorUsage={org.cursor_usage}
            onSetKey={onSetCursorAdminKey}
            onRemoveKey={onRemoveCursorAdminKey}
          />
        )}
      </SectionCard>

      {/* ── Card 7: Sharing with Shift ──────────────────────────────────────
          Each switch's copy says what actually leaves, not "diagnostics" or
          "telemetry". An admin who turns one on should not be surprised by
          what Shift can read. */}
      <SectionCard headerBar title="Sharing with Shift">
        <ToggleRow
          label="Send platform feedback to Shift automatically"
          helper="Sends the title and body of each new report to Shift as it arrives, so the platform team sees it without you forwarding anything. Who submitted it is never sent. Off means nothing leaves until you choose it — use Download or Send to Shift on the Feedback page."
          checked={feedbackAutoShare}
          onCheckedChange={(checked) => {
            setFeedbackAutoShare(checked);
            void Promise.resolve(onToggleFeedbackAutoShare(checked)).catch(() =>
              setFeedbackAutoShare(org.feedback_auto_share),
            );
          }}
        />
        <ToggleRow
          label="Send app crash logs to Shift automatically"
          helper="When one of your apps' containers crashes, sends its last output before the crash to Shift: the app's own log, redacted, up to 256 KiB. Off means a crash log leaves only when you choose Send to Shift on the Feedback page."
          checked={crashLogAutoShare}
          onCheckedChange={(checked) => {
            setCrashLogAutoShare(checked);
            void Promise.resolve(onToggleCrashLogAutoShare(checked)).catch(() =>
              setCrashLogAutoShare(org.crash_log_auto_share),
            );
          }}
        />
      </SectionCard>

      {/* ── Card 8: Supported agents (ADR-0040) ─────────────────────────────── */}
      <SectionCard headerBar title="Supported agents">
        <div className="px-5 pt-5 pb-4">
          <SupportedAgentsEditor editor={supportedAgentsEditor} disabled={savingAgents} />
        </div>
        <div className="flex items-center gap-2 border-t border-border bg-muted/20 px-5 py-3">
          <p className="flex-1 text-xs text-muted-foreground">
            Filters Agent setup and the install wizard handoff — not enforcement.
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="default"
              className="h-11 md:h-9"
              onClick={handleCancelAgents}
              disabled={savingAgents || !supportedAgentsEditor.isDirty}
              data-testid="cancel-supported-agents"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="default"
              className="h-11 md:h-9"
              onClick={() => void handleSaveAgents()}
              disabled={!canSaveAgents || savingAgents}
              data-testid="save-supported-agents"
            >
              {savingAgents ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      </SectionCard>
    </>
  );
}
